import React, { useState } from "react";
import {
  ExternalLink,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  File,
  Edit,
  PencilIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import GradeModal from "./GradeModal";

// Type Definitions
interface SubmissionFile {
  source: string;
  description: string;
  _id: string;
}

interface StudentInfo {
  _id: string;
  fname: string;
  lname: string;
  batch: string;
}

interface AssignmentInfo {
  _id: string;
  title: string;
  type: string;
  deadline: string;
  totalMarks: number;
}

interface Submission {
  _id: string;
  assignmentId: AssignmentInfo;
  batchCode: string;
  studentId: StudentInfo;
  submissionDate: string;
  submissionFile: SubmissionFile[];
  marksObtained: number | null;
  totalMarks: number;
  status: string;
  isLate: boolean;
  createdAt: string;
  updatedAt: string;
}

interface StudentTableProps {
  item: {
    success: boolean;
    data: Submission[];
  };
}

const StudentTable: React.FC<StudentTableProps> = ({ item }) => {
  const [selectedSubmission, setSelectedSubmission] =
    React.useState<string>("");
  // Group submissions by assignment
  const groupedByAssignment = item.reduce((acc, submission) => {
    const assignmentTitle = submission?.assignmentId.title;
    if (!acc[assignmentTitle]) {
      acc[assignmentTitle] = {
        assignment: submission?.assignmentId,
        submissions: [],
      };
    }
    acc[assignmentTitle].submissions.push(submission);
    return acc;
  }, {} as Record<string, { assignment: AssignmentInfo; submissions: Submission[] }>);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string, isLate: boolean) => {
    if (isLate)
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
    switch (status.toLowerCase()) {
      case "submitted":
        return "bg-teal-100 text-[#49ccc1e2] dark:bg-[#49ccc1e2] dark:text-[#26BDB0]";
      case "graded":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };
  return (
    <div className="p-6 max-w-7xl mx-auto font-sans">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
        Student Submissions Dashboard
      </h1>

      <div className="space-y-8">
        {Object.entries(groupedByAssignment).map(
          ([assignmentTitle, { assignment, submissions }]: any) => (
            <div
              key={assignment._id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Assignment Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-black dark:to-black px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-300 flex items-center gap-2">
                      {assignment.title}
                      <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        ({submissions.length} submission
                        {submissions.length !== 1 ? "s" : ""})
                      </span>
                    </h2>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-700 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Deadline: {formatDate(assignment.deadline)}
                      </span>
                      <span>Total Marks: {assignment.totalMarks}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submissions Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-[#1F1F1F] border-b border-gray-200 dark:border-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Batch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Submission Files
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Submitted At
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Marks
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-700">
                    {submissions.map((submission) => (
                      <tr
                        key={submission?._id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                      >
                        {/* Student Name */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {submission?.studentId.fname}{" "}
                            {submission?.studentId.lname}
                          </div>
                        </td>

                        {/* Batch */}
                        <td className="px-6 py-4">
                          <span className="py-1 inline-flex text-xs leading-5 font-semibold text-gray-800 dark:text-gray-300">
                            {submission?.studentId.batch}
                          </span>
                        </td>

                        {/* Submission Files */}
                        <td className="px-6 py-4">
                          <div className="space-y-2 max-w-md">
                            {submission?.submissionFile.map((file, index) => (
                              <div
                                key={file._id}
                                className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <span className="flex-shrink-0 py-1 text-blue-600 ">
                                  <File size={15} />
                                </span>
                                <div className="flex-1 min-w-0">
                                  <a
                                    href={file.source}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm font-medium"
                                  >
                                    <span className="truncate">
                                      File {index + 1}
                                    </span>
                                    <ExternalLink
                                      size={12}
                                      className="flex-shrink-0"
                                    />
                                  </a>
                                  {file.description && (
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                      {file.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                            {submission?.submissionFile.length === 0 && (
                              <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                                No files submitted
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Submission Date */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                            <Clock
                              size={14}
                              className="text-gray-400 dark:text-gray-500"
                            />
                            <span>
                              {formatDate(submission?.submissionDate)}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                              submission?.status,
                              submission?.isLate
                            )}`}
                          >
                            {submission?.isLate ? (
                              <>
                                <AlertCircle size={12} />
                                Late
                              </>
                            ) : (
                              <>
                                <CheckCircle size={12} />
                                {submission?.status}
                              </>
                            )}
                          </span>
                        </td>

                        {/* Marks */}
                        <td className="px-6 py-4 text-center w-40">
                          {submission.marksObtained !== null ? (
                            <div className="flex items-center justify-center gap-6 font-semibold">
                              <span className="  font-semibold text-[#26BDB0]">
                                {submission.marksObtained} /{" "}
                                {submission.totalMarks}
                              </span>
                              <div
                                className="text-blue-600 bg-blue-100 p-2 rounded hover:text-gray-600 cursor-pointer"
                                onClick={() =>
                                  setSelectedSubmission(submission)
                                }
                              >
                                <PencilIcon size={15} />
                              </div>
                            </div>
                          ) : (
                            <Button
                              onClick={() => setSelectedSubmission(submission)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md"
                            >
                              Grade
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}
      </div>
      {selectedSubmission && (
        <GradeModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </div>
  );
};

export default StudentTable;
